export type ImportField =
  | 'full_name'
  | 'source_key'
  | 'email'
  | 'phone'
  | 'primary_platform'
  | 'primary_handle'
  | 'profile_url'
  | 'followers_count'
  | 'fee_amount'
  | 'tags'

export type CsvMapping = Record<ImportField, string>

export const importFieldLabels: Record<ImportField, string> = {
  full_name: 'Họ tên *',
  source_key: 'Mã định danh',
  email: 'Email',
  phone: 'Số điện thoại',
  primary_platform: 'Nền tảng chính',
  primary_handle: 'Handle',
  profile_url: 'Profile URL',
  followers_count: 'Followers',
  fee_amount: 'Fee',
  tags: 'Tags',
}

const aliases: Record<ImportField, string[]> = {
  full_name: ['full_name', 'name', 'creator_name', 'creator', 'ho_ten', 'ten'],
  source_key: ['source_key', 'external_id', 'creator_id', 'id', 'ma_creator'],
  email: ['email', 'mail'],
  phone: ['phone', 'mobile', 'telephone', 'sdt', 'so_dien_thoai'],
  primary_platform: ['primary_platform', 'platform', 'channel', 'nen_tang'],
  primary_handle: ['primary_handle', 'handle', 'username', 'account', 'tai_khoan'],
  profile_url: ['profile_url', 'url', 'profile', 'link', 'social_url'],
  followers_count: ['followers_count', 'followers', 'follower', 'audience'],
  fee_amount: ['fee_amount', 'fee', 'cost', 'rate', 'creator_fee'],
  tags: ['tags', 'tag', 'labels'],
}

function normalizeHeader(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

export function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index]
    const next = text[index + 1]

    if (character === '"') {
      if (quoted && next === '"') {
        cell += '"'
        index += 1
      } else {
        quoted = !quoted
      }
    } else if (character === ',' && !quoted) {
      row.push(cell.trim())
      cell = ''
    } else if ((character === '\n' || character === '\r') && !quoted) {
      if (character === '\r' && next === '\n') index += 1
      row.push(cell.trim())
      if (row.some((value) => value !== '')) rows.push(row)
      row = []
      cell = ''
    } else {
      cell += character
    }
  }

  row.push(cell.trim())
  if (row.some((value) => value !== '')) rows.push(row)
  return rows
}

export function suggestMapping(headers: string[]): CsvMapping {
  const normalized = headers.map(normalizeHeader)
  return Object.fromEntries(
    (Object.keys(aliases) as ImportField[]).map((field) => {
      const index = normalized.findIndex((header) => aliases[field].includes(header))
      return [field, index >= 0 ? headers[index] : '']
    }),
  ) as CsvMapping
}

export function buildImportPayload(headers: string[], rows: string[][], mapping: CsvMapping) {
  const indexByHeader = new Map(headers.map((header, index) => [header, index]))
  const mappedHeaders = new Set(Object.values(mapping).filter(Boolean))

  return rows.map((row) => {
    const value = (field: ImportField) => {
      const header = mapping[field]
      const index = header ? indexByHeader.get(header) : undefined
      return index === undefined ? '' : (row[index] ?? '').trim()
    }

    const email = value('email')
    const phone = value('phone')
    const platform = value('primary_platform')
    const handle = value('primary_handle')
    const explicitSourceKey = value('source_key')
    const sourceKey = explicitSourceKey || email.toLowerCase() || phone || [platform, handle].filter(Boolean).join(':').toLowerCase()
    const tags = value('tags')
      .split(/[;|]/)
      .map((tag) => tag.trim())
      .filter(Boolean)

    const customFields = Object.fromEntries(
      headers
        .filter((header) => !mappedHeaders.has(header))
        .map((header) => [header, row[indexByHeader.get(header) ?? -1] ?? ''])
        .filter(([, fieldValue]) => fieldValue !== ''),
    )

    return {
      source_key: sourceKey,
      full_name: value('full_name'),
      email,
      phone,
      primary_platform: platform,
      primary_handle: handle,
      profile_url: value('profile_url'),
      followers_count: value('followers_count'),
      fee_amount: value('fee_amount'),
      tags,
      custom_fields: customFields,
    }
  })
}
